import orderBy from "lodash-es/orderBy.js";
import uniqBy from "lodash-es/uniqBy.js";
import { css, html } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@lit-labs/virtualizer";

import { Style } from "../Styles";
import { BaseElement } from "../BaseElement";
import { IJournal } from "../../backend/cloud/JournalTable";
import { ChangeSet, ChangeSetListener } from "../../DataStore/ChangeSet";
import { DataProvider } from "../../DataStore/DataProvider";
import { DataClient } from "../../DataStore/LocalDataStore";

import "./Journal";
import { ModificationAction, ModificationModel } from "../../../models";
import { filter } from "async";

const MAX_ENTRIES = 64;

@customElement("ds-journal-list")
export class JournalList extends BaseElement implements ChangeSetListener {
    static override styles = [
        Style,
        css`
            :host {
                display: block;
            }

            ds-journal {
                width: 100%;
                margin-bottom: 1em;
            }
        `,
    ];

    @state() protected _journals?: IJournal[];

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();

        this._kernel.dataStore.connectClient(
            new DataClient(this, [{ source: "journals", paths: [] }])
        );

        this._updateEntries(undefined);
    }

    async onChangeSet(_source: DataProvider, _changeSet: ChangeSet<any>) {
        this._updateEntries(undefined);
    }

    private async _updateEntries(entries: IJournal[] | undefined) {
        let journals: IJournal[] | undefined;

        if (!entries) {
            entries = Array.from(await this._kernel.dataStore.getSource("journals").all());
        }
        if (entries) {
            journals = [...entries];
        }
        if (journals) {
            // remove all entries that refer to places that do not exist (except deletes)
            const placeTable = this._kernel.dataStore.getSource("places");
            journals = await filter(journals, async (j: IJournal) => {
                const mod = j.modification;
                if (this._kernel.isInCollection(mod.collections)) {
                    if (
                        mod.model === ModificationModel.PLACE &&
                        mod.action !== ModificationAction.DELETE
                    ) {
                        const placeId = mod.modelId;
                        return (await placeTable.read(placeId)) !== undefined;
                    }
                    return true;
                } else {
                    return false;
                }
                return true;
            });

            // only show the latest entries
            journals = orderBy(journals, ["addedAt"], ["desc"]);
            journals = uniqBy(journals, (x) => x.modification.modelId ?? x.id);
            journals = orderBy(journals, ["addedAt"], ["desc"]);
            if (journals.length > MAX_ENTRIES) {
                journals = journals.slice(0, MAX_ENTRIES);
            }
        }
        this._journals = journals;
    }
    override render() {
        return html`<div class="flex-column">
            <lit-virtualizer
                .items=${this._journals ?? []}
                .renderItem=${(x: IJournal) => html` <ds-journal .journal=${x}></ds-journal> `}
            ></lit-virtualizer>
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-journal-list": JournalList;
    }
}
